import { useCallback, useEffect, useMemo, useState } from 'react';
import { compileVisualPrompt } from '@/utils/visualPrompts/compileVisualPrompt';
import { getVisibleSteps } from '@/utils/visualPrompts/evaluateStepVisibility';
import { validateWizardStep } from '@/utils/visualPrompts/validateWizardStep';
import type {
  VisualPromptAnswers,
  VisualPromptAnswerValue,
  VisualPromptDefinition,
  VisualPromptStep,
} from '@/types/visualPrompts/visualPrompt.types';
import { VISUAL_PROMPT_MESSAGES } from '@/constants/visualPrompts/messages';
import { mergeSavedChoicesWithDefaults } from '@/utils/visualPrompts/visualPromptStorage';

const buildInitialAnswers = (
  definition: VisualPromptDefinition | null,
): VisualPromptAnswers => {
  if (!definition) {
    return {};
  }
  return mergeSavedChoicesWithDefaults(definition.id, definition.defaults);
};

export const useVisualPromptWizard = (
  definition: VisualPromptDefinition | null,
) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState<VisualPromptAnswers>(() =>
    buildInitialAnswers(definition),
  );
  const [stepError, setStepError] = useState<string | null>(null);

  useEffect(() => {
    if (definition) {
      setCurrentStepIndex(0);
      setAnswers(buildInitialAnswers(definition));
      setStepError(null);
    }
  }, [definition?.id]);

  const visibleSteps = useMemo(
    () => (definition ? getVisibleSteps(definition.steps, answers) : []),
    [definition, answers],
  );

  const currentStep: VisualPromptStep | undefined =
    visibleSteps[currentStepIndex];

  const compiledPreview = useMemo(() => {
    if (!definition) {
      return '';
    }
    try {
      return compileVisualPrompt(definition, answers);
    } catch {
      return '';
    }
  }, [definition, answers]);

  const isLastStep = currentStepIndex >= visibleSteps.length - 1;
  const isReviewStep = currentStep?.type === 'review';

  const resetWizard = useCallback((def: VisualPromptDefinition | null) => {
    setCurrentStepIndex(0);
    setAnswers(buildInitialAnswers(def));
    setStepError(null);
  }, []);

  const setAnswer = useCallback(
    (mapsTo: string, value: VisualPromptAnswerValue) => {
      setAnswers((prev) => ({ ...prev, [mapsTo]: value }));
      setStepError(null);
    },
    [],
  );

  const canProceed = useCallback((): boolean => {
    if (!currentStep) {
      return false;
    }
    return validateWizardStep(currentStep, answers).valid;
  }, [currentStep, answers]);

  const goNext = useCallback((): boolean => {
    if (!currentStep) {
      return false;
    }
    const validation = validateWizardStep(currentStep, answers);
    if (!validation.valid) {
      setStepError(validation.message ?? VISUAL_PROMPT_MESSAGES.requiredField);
      return false;
    }
    setStepError(null);
    if (currentStepIndex < visibleSteps.length - 1) {
      setCurrentStepIndex((i) => i + 1);
    }
    return true;
  }, [currentStep, answers, currentStepIndex, visibleSteps.length]);

  const goBack = useCallback(() => {
    setStepError(null);
    if (currentStepIndex > 0) {
      setCurrentStepIndex((i) => i - 1);
    }
  }, [currentStepIndex]);

  return {
    visibleSteps,
    currentStepIndex,
    currentStep,
    answers,
    setAnswer,
    compiledPreview,
    isLastStep,
    isReviewStep,
    stepError,
    resetWizard,
    canProceed,
    goNext,
    goBack,
  };
};
